// Copyright (c) 2026 AesirX.io Ltd. All rights reserved.
// This source code is proprietary and confidential.
// See LICENSE.md for licensing terms.
/**
 * Sprint 63.28.0 (A1, OQ-1) — the two closed vocabularies Persona Paths groups
 * and filters by.
 *
 * Before this file the groupings existed only as comments in the registry
 * ("P-VN-17…27 — authored in Sprint 62.0.0"), which describe when a persona was
 * written, not what job it is. A picker over 53 roles needs to say what job.
 *
 * **Role family** is the kind of work the person does; **sector** is the
 * industry the organisation is in. Both describe a job, not an instrument, so
 * neither names a framework, an instrument number or an authority (R-46). Which
 * frameworks name a persona stays pack-declared (`persona_relevance`) and is
 * read with `resolvePersonaRelevance`; nothing here duplicates it.
 *
 * Both lists were fixed by reading the role, context and region of every
 * document in `docs/user-validation/personas/`. Each key carries EN and VI
 * labels (R-57: EN and VI only; other locales fall back to EN through
 * `extractLocale`). `persona-taxonomy.test.ts` fails on a registry entry whose
 * family or sector is not a key here, and on a key no persona uses.
 */
/**
 * At most seven families; every persona has exactly one.
 *
 * Order is display order: the families most organisations start from first.
 */
export const PERSONA_ROLE_FAMILIES = {
    privacy_data_protection: {
        en: 'Privacy & data protection',
        vi: 'Quyền riêng tư và bảo vệ dữ liệu cá nhân',
    },
    data_ai_governance: {
        en: 'Data & AI governance',
        vi: 'Quản trị dữ liệu và AI',
    },
    security_cyber: {
        en: 'Security & cyber resilience',
        vi: 'An ninh mạng và an toàn thông tin',
    },
    audit_legal_assurance: {
        en: 'Audit, legal & assurance',
        vi: 'Kiểm toán, pháp lý và đánh giá độc lập',
    },
    sector_regulatory: {
        en: 'Sector regulatory compliance',
        vi: 'Tuân thủ quy định chuyên ngành',
    },
    operations_records: {
        en: 'Operations & records',
        vi: 'Vận hành và hồ sơ',
    },
    executive_business: {
        en: 'Executives & business owners',
        vi: 'Lãnh đạo và chủ doanh nghiệp',
    },
};
/** Every role-family key, in display order. */
export const PERSONA_ROLE_FAMILY_KEYS = Object.keys(PERSONA_ROLE_FAMILIES);
/**
 * A persona has one or more sectors. `cross_sector` marks a role that exists
 * in any organisation (a DPO, an internal auditor), so a sector filter never
 * hides it from an organisation whose own sector it does not name.
 */
export const PERSONA_SECTORS = {
    cross_sector: { en: 'Cross-sector', vi: 'Đa ngành' },
    banking_payments: { en: 'Banking & payments', vi: 'Ngân hàng và thanh toán' },
    fintech: { en: 'Fintech', vi: 'Công nghệ tài chính' },
    crypto_assets: { en: 'Crypto-assets', vi: 'Tài sản mã hóa' },
    securities: { en: 'Securities & capital markets', vi: 'Chứng khoán và thị trường vốn' },
    insurance: { en: 'Insurance', vi: 'Bảo hiểm' },
    ecommerce_retail: { en: 'E-commerce & retail', vi: 'Thương mại điện tử và bán lẻ' },
    telecom_postal: { en: 'Telecommunications & postal', vi: 'Viễn thông và bưu chính' },
    technology_saas: { en: 'Technology & SaaS', vi: 'Công nghệ và dịch vụ phần mềm' },
    education: { en: 'Education', vi: 'Giáo dục' },
    health: { en: 'Health', vi: 'Y tế' },
    public_sector: { en: 'Public sector', vi: 'Khu vực công' },
    manufacturing_quality: {
        en: 'Manufacturing & product quality',
        vi: 'Sản xuất và chất lượng sản phẩm',
    },
    food: { en: 'Food', vi: 'Thực phẩm' },
    chemicals_environment: { en: 'Chemicals & environment', vi: 'Hóa chất và môi trường' },
    logistics_customs: { en: 'Logistics & customs', vi: 'Logistics và hải quan' },
    transport: { en: 'Transport', vi: 'Giao thông vận tải' },
    energy_utilities: { en: 'Energy & utilities', vi: 'Năng lượng và hạ tầng tiện ích' },
    publishing_media: { en: 'Publishing & media', vi: 'Xuất bản và truyền thông' },
    legal_services: { en: 'Legal services', vi: 'Dịch vụ pháp lý' },
    trust_cryptography: {
        en: 'Digital trust & cryptography',
        vi: 'Dịch vụ tin cậy và mật mã',
    },
};
/** Every sector key, in display order. */
export const PERSONA_SECTOR_KEYS = Object.keys(PERSONA_SECTORS);
//# sourceMappingURL=persona-taxonomy.js.map